/**
 * chartMarks — the mark specs the kit's SVG charts share, in one place.
 *
 * Fixed across every chart (dataviz mark spec): bars cap at 24px and never fill
 * their band, a bar's data-end is 4px-rounded while its baseline stays square,
 * lines are 2px with round joins, dots are >=8px across and carry a 2px ring in
 * the surface colour so they stay legible where they cross a line, and gridlines
 * are hairline SOLID — a dashed grid competes with dashed target rules, which in
 * this app carry meaning.
 */

export const BAR_RADIUS = 4
export const LINE_W = 2
export const DOT_R = 4
/** The 2px surface ring/gap that does the separating — never a stroke of ink. */
export const SURFACE_GAP = 2

/**
 * Bar width for a band: a FRACTION of the slot, so a bar grows with the chart.
 *
 * This used to be capped at 24px. The cap meant the wider the panel, the more of it was
 * gap — a 120px month band carrying a 24px bar is 96px of nothing, and the chart visibly
 * stopped using the space it had been given. A share of the band is what "scales with its
 * container" actually means.
 */
export function barWidth(step: number): number {
  return Math.max(3, step * 0.56)
}

/**
 * A column growing from a baseline: rounded at the data-end, square where it meets
 * the axis. A mark that touches nothing (a floating waterfall step) is not this — it
 * rounds both ends, which is a plain rounded rect.
 */
export function barPath(x: number, y: number, w: number, h: number): string {
  if (h <= 0) return ''
  const r = Math.max(0, Math.min(BAR_RADIUS, w / 2, h))
  return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`
}

const STEPS = [1, 2, 2.5, 5, 10]

/**
 * An axis covering [lo, hi] in `divisions` steps of a round size (1 / 2 / 2.5 / 5 ×
 * a power of ten). Ticks carry the values that are not directly labelled, so they
 * have to be numbers a reader can hold — 0 / 250k / 500k, never 0 / 237k / 474k.
 *
 * A fixed division count is what lets two axes share one set of gridlines: both get
 * round numbers, on the same rows, with no second grid drawn over the first.
 */
export function axisScale(lo: number, hi: number, divisions = 4): { lo: number; step: number } {
  const range = hi - lo
  const span = range > 0 ? range : Math.max(Math.abs(hi), 1)
  let mag = 10 ** Math.floor(Math.log10(span / divisions))
  for (let decade = 0; decade < 14; decade += 1) {
    for (const m of STEPS) {
      const step = m * mag
      const base = Math.floor(lo / step) * step
      if (base + step * divisions >= hi) return { lo: base, step }
    }
    mag *= 10
  }
  return { lo, step: (hi - lo) / divisions || 1 }
}

/** Label every nth category so x labels never collide — the rest are in the tooltip. */
export function labelStride(count: number, plotWidth: number, minGap = 46): number {
  if (count < 2) return 1
  return Math.max(1, Math.ceil(minGap / (plotWidth / count)))
}
